import { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  Typography,
  LinearProgress,
  Chip,
  Tooltip,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import {
  DomainAssessment,
  AuditDomainLabel,
  AuditDomainWHOBlock,
  MaturityLevel,
  MaturityLevelLabel,
  MaturityLevelDescription,
} from '../../types';
import { auditService } from '../../services/auditService';

const useStyles = makeStyles(theme => ({
  maturityScale: {
    display: 'flex',
    marginBottom: theme.spacing(4),
    gap: theme.spacing(1),
  },
  maturityLevelCard: {
    flex: 1,
    padding: theme.spacing(2),
    textAlign: 'center',
    borderRadius: theme.shape.borderRadius,
    transition: 'transform 0.2s ease',
  },
  levelNumber: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#fff',
  },
  levelName: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: '#fff',
    marginTop: 2,
  },
  levelDesc: {
    fontSize: '0.65rem',
    color: 'rgba(255,255,255,0.85)',
    marginTop: 4,
  },
  domainMaturityCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  domainHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1.5),
  },
  domainTitle: {
    fontWeight: 600,
  },
  maturityTrack: {
    display: 'flex',
    alignItems: 'center',
    height: 48,
    position: 'relative',
    marginBottom: theme.spacing(1),
  },
  trackSegment: {
    flex: 1,
    height: 12,
    borderRadius: 6,
    margin: '0 2px',
    position: 'relative',
  },
  currentMarker: {
    position: 'absolute',
    top: -18,
    transform: 'translateX(-50%)',
    fontSize: '0.7rem',
    fontWeight: 'bold',
    padding: '2px 8px',
    borderRadius: 10,
    color: '#fff',
    whiteSpace: 'nowrap',
  },
  targetMarker: {
    position: 'absolute',
    bottom: -18,
    transform: 'translateX(-50%)',
    fontSize: '0.7rem',
    fontWeight: 'bold',
    padding: '2px 8px',
    borderRadius: 10,
    color: '#fff',
    backgroundColor: '#1976d2',
    whiteSpace: 'nowrap',
  },
  gapIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  gapArrow: {
    fontSize: 16,
    color: theme.palette.text.secondary,
  },
  summaryCard: {
    padding: theme.spacing(3),
    textAlign: 'center',
    height: '100%',
  },
  summaryValue: {
    fontSize: '2rem',
    fontWeight: 'bold',
  },
  summaryLabel: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
  },
  whoBlock: {
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
  },
  radarContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(3),
  },
}));

const maturityColors: Record<number, string> = {
  1: '#d32f2f',
  2: '#f44336',
  3: '#ff9800',
  4: '#8bc34a',
  5: '#4caf50',
};

export const MaturityView = () => {
  const classes = useStyles();
  const [domains, setDomains] = useState<DomainAssessment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    auditService.getDomainAssessments().then(d => {
      setDomains(d);
      setLoading(false);
    });
  }, []);

  if (loading) return <LinearProgress />;

  const avgCurrent =
    domains.reduce((s, d) => s + d.currentMaturity, 0) / domains.length;
  const avgTarget =
    domains.reduce((s, d) => s + d.targetMaturity, 0) / domains.length;
  const gapDomains = domains.filter(
    d => d.targetMaturity - d.currentMaturity >= 2,
  );
  const lowestDomain = domains.reduce(
    (min, d) => (d.currentMaturity < min.currentMaturity ? d : min),
    domains[0],
  );

  // Simple radar visualization using SVG
  const radarSize = 300;
  const centerX = radarSize / 2;
  const centerY = radarSize / 2;
  const maxRadius = radarSize / 2 - 40;
  const angleStep = (2 * Math.PI) / domains.length;

  const getPoint = (index: number, value: number) => {
    const angle = angleStep * index - Math.PI / 2;
    const r = (value / 5) * maxRadius;
    return {
      x: centerX + r * Math.cos(angle),
      y: centerY + r * Math.sin(angle),
    };
  };

  const currentPoints = domains.map((d, i) => getPoint(i, d.currentMaturity));
  const targetPoints = domains.map((d, i) => getPoint(i, d.targetMaturity));
  const currentPath = `${currentPoints
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`)
    .join(' ')} Z`;
  const targetPath = `${targetPoints
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`)
    .join(' ')} Z`;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Digital Health Maturity Model — WHO-Aligned Assessment
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Based on the WHO MAPS toolkit maturity framework. Each domain is
        assessed on a 5-level scale progressing from Ad-hoc practices to
        continuous Optimizing processes.
      </Typography>

      {/* Maturity scale legend */}
      <div className={classes.maturityScale}>
        {[1, 2, 3, 4, 5].map(level => (
          <Tooltip
            key={level}
            title={MaturityLevelDescription[level as MaturityLevel]}
            arrow
          >
            <Card
              className={classes.maturityLevelCard}
              style={{ backgroundColor: maturityColors[level] }}
              elevation={2}
            >
              <Typography className={classes.levelNumber}>{level}</Typography>
              <Typography className={classes.levelName}>
                {MaturityLevelLabel[level as MaturityLevel]}
              </Typography>
              <Typography className={classes.levelDesc}>
                {MaturityLevelDescription[level as MaturityLevel].substring(
                  0,
                  60,
                )}
                …
              </Typography>
            </Card>
          </Tooltip>
        ))}
      </div>

      {/* Summary KPIs */}
      <Grid container spacing={2} style={{ marginBottom: 24 }}>
        <Grid item xs={6} sm={3}>
          <Card className={classes.summaryCard} variant="outlined">
            <Typography
              className={classes.summaryValue}
              style={{ color: maturityColors[Math.round(avgCurrent)] }}
            >
              {avgCurrent.toFixed(1)}
            </Typography>
            <Typography className={classes.summaryLabel}>
              Avg Current Maturity
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card className={classes.summaryCard} variant="outlined">
            <Typography
              className={classes.summaryValue}
              style={{ color: '#1976d2' }}
            >
              {avgTarget.toFixed(1)}
            </Typography>
            <Typography className={classes.summaryLabel}>
              Avg Target Maturity
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card className={classes.summaryCard} variant="outlined">
            <Typography
              className={classes.summaryValue}
              style={{ color: '#f44336' }}
            >
              {gapDomains.length}
            </Typography>
            <Typography className={classes.summaryLabel}>
              Domains with Gap ≥ 2
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card className={classes.summaryCard} variant="outlined">
            <Typography
              className={classes.summaryValue}
              style={{ color: '#d32f2f' }}
            >
              L{lowestDomain.currentMaturity}
            </Typography>
            <Typography className={classes.summaryLabel}>
              Lowest: {AuditDomainLabel[lowestDomain.domain]}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Radar chart */}
        <Grid item xs={12} md={5}>
          <Card variant="outlined" style={{ padding: 16 }}>
            <Typography
              variant="subtitle1"
              style={{ fontWeight: 600, textAlign: 'center' }}
            >
              Maturity Radar
            </Typography>
            <div className={classes.radarContainer}>
              <svg width={radarSize} height={radarSize}>
                {/* Grid circles */}
                {[1, 2, 3, 4, 5].map(level => (
                  <circle
                    key={level}
                    cx={centerX}
                    cy={centerY}
                    r={(level / 5) * maxRadius}
                    fill="none"
                    stroke="#e0e0e0"
                    strokeWidth="0.5"
                  />
                ))}
                {/* Axis lines */}
                {domains.map((_, i) => {
                  const p = getPoint(i, 5);
                  return (
                    <line
                      key={i}
                      x1={centerX}
                      y1={centerY}
                      x2={p.x}
                      y2={p.y}
                      stroke="#e0e0e0"
                      strokeWidth="0.5"
                    />
                  );
                })}
                {/* Target polygon */}
                <path
                  d={targetPath}
                  fill="rgba(25,118,210,0.1)"
                  stroke="#1976d2"
                  strokeWidth="1.5"
                  strokeDasharray="4,4"
                />
                {/* Current polygon */}
                <path
                  d={currentPath}
                  fill="rgba(244,67,54,0.15)"
                  stroke="#f44336"
                  strokeWidth="2"
                />
                {/* Domain labels */}
                {domains.map((d, i) => {
                  const p = getPoint(i, 5.8);
                  return (
                    <text
                      key={d.domain}
                      x={p.x}
                      y={p.y}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fontSize="8"
                      fill="#666"
                    >
                      {AuditDomainLabel[d.domain].split(' ')[0]}
                    </text>
                  );
                })}
                {/* Current dots */}
                {currentPoints.map((p, i) => (
                  <circle key={i} cx={p.x} cy={p.y} r="4" fill="#f44336" />
                ))}
              </svg>
            </div>
            <Box
              display="flex"
              justifyContent="center"
              mt={1}
              style={{ gap: '16px' }}
            >
              <Typography variant="caption" style={{ color: '#f44336' }}>
                ● Current Maturity
              </Typography>
              <Typography variant="caption" style={{ color: '#1976d2' }}>
                --- Target Maturity
              </Typography>
            </Box>
          </Card>
        </Grid>

        {/* Domain maturity cards */}
        <Grid item xs={12} md={7}>
          {domains.map(d => {
            const gap = d.targetMaturity - d.currentMaturity;
            return (
              <Card
                key={d.domain}
                className={classes.domainMaturityCard}
                variant="outlined"
              >
                <div className={classes.domainHeader}>
                  <div>
                    <Typography className={classes.domainTitle}>
                      {AuditDomainLabel[d.domain]}
                    </Typography>
                    <Typography className={classes.whoBlock}>
                      WHO: {AuditDomainWHOBlock[d.domain]}
                    </Typography>
                  </div>
                  <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                    Score: {d.score}%
                  </Typography>
                </div>

                {/* 5-level track */}
                <div className={classes.maturityTrack}>
                  {[1, 2, 3, 4, 5].map(level => (
                    <Tooltip
                      key={level}
                      title={`${MaturityLevelLabel[level as MaturityLevel]}: ${
                        MaturityLevelDescription[level as MaturityLevel]
                      }`}
                      arrow
                    >
                      <div
                        className={classes.trackSegment}
                        style={{
                          backgroundColor:
                            level <= d.currentMaturity
                              ? maturityColors[level]
                              : level <= d.targetMaturity
                              ? 'rgba(25,118,210,0.2)'
                              : '#e0e0e0',
                          position: 'relative',
                        }}
                      >
                        {level === d.currentMaturity && (
                          <span
                            className={classes.currentMarker}
                            style={{
                              backgroundColor: maturityColors[level],
                              left: '50%',
                            }}
                          >
                            Current: L{level}
                          </span>
                        )}
                        {level === d.targetMaturity && (
                          <span
                            className={classes.targetMarker}
                            style={{ left: '50%' }}
                          >
                            Target: L{level}
                          </span>
                        )}
                      </div>
                    </Tooltip>
                  ))}
                </div>

                <div className={classes.gapIndicator}>
                  <Chip
                    size="small"
                    label={`L${d.currentMaturity} ${
                      MaturityLevelLabel[d.currentMaturity]
                    }`}
                    style={{
                      backgroundColor: maturityColors[d.currentMaturity],
                      color: '#fff',
                      fontWeight: 'bold',
                    }}
                  />
                  <ArrowForwardIcon className={classes.gapArrow} />
                  <Chip
                    size="small"
                    label={`L${d.targetMaturity} ${
                      MaturityLevelLabel[d.targetMaturity]
                    }`}
                    style={{
                      backgroundColor: '#1976d2',
                      color: '#fff',
                      fontWeight: 'bold',
                    }}
                  />
                  <Chip
                    size="small"
                    label={`Gap: ${gap} level${gap > 1 ? 's' : ''}`}
                    variant="outlined"
                    style={{
                      borderColor: gap >= 2 ? '#f44336' : '#ff9800',
                      color: gap >= 2 ? '#f44336' : '#ff9800',
                      fontWeight: 'bold',
                    }}
                  />
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    style={{ marginLeft: 'auto' }}
                  >
                    {d.findingsCount} findings ({d.criticalFindings} critical)
                  </Typography>
                </div>
              </Card>
            );
          })}
        </Grid>
      </Grid>
    </Box>
  );
};
